"use client";

/**
 * IconOrEmoji – Renders stored icon value as either Lucide icon or emoji.
 * Format: emoji = Unicode string, icon = "lucide:icon-name" (kebab-case).
 */

// One registry and one normalizer, owned by the backend (plan 14 §4); the
// display mode keeps unknown Lucide names instead of throwing.
import { resolveLucideIconKey } from "@v1/backend/logic/icons/lucide_icon_registry.generated";
import { normalizeStoredIconValueForDisplay } from "@v1/backend/logic/icons/normalize_stored_icon_value";
import type { LucideIcon } from "lucide-react";
import dynamicIconImports from "lucide-react/dynamicIconImports";
import * as React from "react";

import { cn } from "../utils";
import {
  AGENT_MARK_ICON_VALUE,
  AgentMarkIcon,
  type AgentMarkIconVariant,
} from "./agent-mark-icon";

const LUCIDE_PREFIX = "lucide:";

export function isAgentMarkIconValue(value: string): boolean {
  return value === AGENT_MARK_ICON_VALUE || value === "lucide:bot";
}

const dynamicImports = dynamicIconImports as Record<
  string,
  () => Promise<{ default: LucideIcon }>
>;

function resolveLucideDynamicImportKey(rawName: string): string | undefined {
  const resolved: string | null = resolveLucideIconKey(rawName);
  if (resolved === null) return undefined;
  if (resolved in dynamicImports) return resolved;
  return undefined;
}

function lucideRawNameFromStoredValue(value: string): string {
  const normalized: string | undefined =
    normalizeStoredIconValueForDisplay(value);
  const stored: string = normalized ?? value;
  return stored.startsWith(LUCIDE_PREFIX)
    ? stored.slice(LUCIDE_PREFIX.length)
    : stored;
}

export function isLucideIcon(value: string | undefined): boolean {
  if (!value) return false;
  const rawName: string = lucideRawNameFromStoredValue(value);
  return resolveLucideDynamicImportKey(rawName) !== undefined;
}

export function getLucideIconName(value: string): string {
  const rawName: string = lucideRawNameFromStoredValue(value);
  const resolved: string | undefined = resolveLucideDynamicImportKey(rawName);
  return resolved ?? rawName;
}

export interface IconOrEmojiProps {
  value: string | undefined;
  fallback?: string;
  size?: number;
  className?: string;
  /** Only applies to {@link AGENT_MARK_ICON_VALUE}: the one agent triangle shape. */
  agentMarkProportionalCorners?: boolean;
  /** Only applies to {@link AGENT_MARK_ICON_VALUE}; default `outline` (Lucide-style). */
  agentMarkVariant?: AgentMarkIconVariant;
  /**
   * Only applies to {@link AGENT_MARK_ICON_VALUE}.
   * `end` bottom-aligns the mark with adjacent text in flex rows (e.g. bookmark pills).
   */
  agentMarkInlineAlign?: "center" | "end";
}

export function IconOrEmoji({
  value,
  fallback = "📄",
  size = 18,
  className,
  agentMarkProportionalCorners,
  agentMarkVariant = "outline",
  agentMarkInlineAlign = "center",
}: IconOrEmojiProps) {
  const normalizedValue: string | undefined =
    normalizeStoredIconValueForDisplay(value);

  if (!normalizedValue) {
    return (
      <span
        className={cn("inline-flex shrink-0 leading-none", className)}
        style={{ fontSize: size }}
      >
        {fallback}
      </span>
    );
  }

  if (isAgentMarkIconValue(normalizedValue)) {
    return (
      <AgentMarkIcon
        size={size}
        className={cn(
          className,
          agentMarkInlineAlign === "end" ? "self-end" : undefined,
        )}
        proportionalCorners={agentMarkProportionalCorners}
        variant={agentMarkVariant}
        alignBottomInBox={agentMarkInlineAlign === "end"}
      />
    );
  }

  if (!isLucideIcon(normalizedValue)) {
    const displayValue: string = normalizedValue.startsWith(LUCIDE_PREFIX)
      ? fallback
      : normalizedValue;
    return (
      <span
        className={cn("inline-flex shrink-0 leading-none", className)}
        style={{ fontSize: size }}
      >
        {displayValue}
      </span>
    );
  }

  const name = getLucideIconName(normalizedValue);
  return (
    <LazyLucideIcon
      name={name}
      size={size}
      className={className}
      fallback={fallback}
    />
  );
}

function LazyLucideIcon({
  name,
  size,
  className,
  fallback,
}: {
  name: string;
  size: number;
  className?: string;
  fallback: string;
}) {
  const [Icon, setIcon] = React.useState<LucideIcon | null>(null);
  const [isInvalidIcon, setIsInvalidIcon] = React.useState<boolean>(false);

  React.useEffect(() => {
    let cancelled = false;
    setIsInvalidIcon(false);
    setIcon(null);
    const loader = dynamicImports[name];
    if (!loader) {
      setIsInvalidIcon(true);
      return () => {
        cancelled = true;
      };
    }
    void loader()
      .then((mod) => {
        if (cancelled) return;
        setIcon(() => mod.default);
      })
      .catch(() => {
        if (cancelled) return;
        setIsInvalidIcon(true);
      });
    return () => {
      cancelled = true;
    };
  }, [name]);

  // A loaded icon wins. A stale failure from the previous name must not
  // replace it with the page fallback.
  if (Icon != null) {
    return (
      <Icon size={size} className={cn("shrink-0", className)} aria-hidden />
    );
  }

  if (isInvalidIcon) {
    return (
      <span
        className={cn("inline-flex shrink-0 leading-none", className)}
        style={{ fontSize: size }}
      >
        {fallback}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 animate-pulse rounded bg-muted",
        className,
      )}
      style={{ width: size, height: size }}
    />
  );
}
